import React, { useState } from "react";
import type { Teammate } from "../../hooks/data-fetching/useMvpVoting";

// =============================================================================
// MVP VOTING MODAL — Cast your monthly MVP vote
// =============================================================================
// Randomized teammate list (bias-free). One-click vote with confirmation.
// Shows "You voted for X" after casting. Can change vote (RPC supports upsert).
// =============================================================================

interface MvpVotingModalProps {
  isOpen: boolean;
  onClose: () => void;
  teammates: Teammate[];
  myVote: { nominee_id: string; nominee_name: string } | null;
  castVote: (nomineeId: string) => Promise<{ success: boolean; error?: string }>;
  votingOpen: boolean;
  daysUntilOpen: number;
  monthLabel: string;
}

// Role badges — desaturated but distinct. Not semantic status, just identity.
const ROLE_BADGE: Record<string, { label: string; bg: string; text: string; avatarBg: string }> = {
  closer: {
    label: "Closer",
    bg: "rgba(45, 120, 80, 0.08)",
    text: "#4a8c65",
    avatarBg: "rgba(45, 120, 80, 0.10)",
  },
  setter: {
    label: "Setter",
    bg: "rgba(90, 70, 140, 0.08)",
    text: "#7a6aad",
    avatarBg: "rgba(90, 70, 140, 0.10)",
  },
  dialer: {
    label: "Dialer",
    bg: "rgba(50, 100, 160, 0.08)",
    text: "#5a8ab5",
    avatarBg: "rgba(50, 100, 160, 0.10)",
  },
};

const MvpVotingModal: React.FC<MvpVotingModalProps> = ({
  isOpen,
  onClose,
  teammates,
  myVote,
  castVote,
  votingOpen,
  daysUntilOpen,
  monthLabel,
}) => {
  const [confirmTarget, setConfirmTarget] = useState<Teammate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleVote = async (teammate: Teammate) => {
    setConfirmTarget(teammate);
  };

  const handleConfirm = async () => {
    if (!confirmTarget) return;
    setIsSubmitting(true);
    setErrorMsg(null);
    const result = await castVote(confirmTarget.user_id);
    setIsSubmitting(false);
    if (!result.success) {
      setErrorMsg(result.error || "Vote failed");
    } else {
      setConfirmTarget(null);
    }
  };

  const handleCancel = () => {
    setConfirmTarget(null);
    setErrorMsg(null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative liquid-glass rounded-[20px] w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-black/[0.06]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="token-18h text-glass">MVP Vote</h2>
              <p className="token-11 text-glass-muted mt-0.5">{monthLabel}</p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap w-7 h-7 flex items-center justify-center rounded-lg hover:bg-black/[0.06] transition-colors text-glass-muted"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Already voted banner */}
          {myVote && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-black/[0.03] border border-black/[0.06]">
              <p className="token-13 text-glass">
                You voted for <strong>{myVote.nominee_name}</strong>
              </p>
              <p className="token-10 text-glass-muted mt-0.5">
                You can change your vote by selecting someone else below.
              </p>
            </div>
          )}
        </div>

        {/* Confirmation overlay */}
        {confirmTarget && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-[20px]">
            <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm text-center shadow-xl">
              <p className="token-18h" style={{ color: "#1a1a1a" }}>
                Vote for <strong>{confirmTarget.full_name}</strong>?
              </p>
              <p className="token-13 mt-1 capitalize" style={{ color: "#888" }}>
                {confirmTarget.role}
              </p>
              {errorMsg && (
                <p className="token-11 mt-3 px-3 py-1.5 rounded-lg bg-red-50 text-red-600">
                  {errorMsg}
                </p>
              )}
              <div className="flex gap-3 mt-5 justify-center">
                <button
                  onClick={handleCancel}
                  className="anim-tap px-5 py-2.5 rounded-xl token-13 font-medium border border-black/10 hover:bg-black/[0.03] transition-colors"
                  style={{ color: "#666" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  className="anim-tap px-5 py-2.5 rounded-xl token-13 font-medium text-white bg-black hover:bg-black/85 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Voting..." : "Confirm Vote"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Teammate list */}
        <div className="detail-overlay overflow-y-auto flex-1 p-3 space-y-1.5">
          {!votingOpen && daysUntilOpen > 0 && (
            <div className="px-3 py-2 rounded-lg bg-black/[0.03] border border-black/[0.06] mb-2">
              <p className="token-11 text-glass-muted">
                Voting opens in {daysUntilOpen} day{daysUntilOpen !== 1 ? "s" : ""}. You&apos;re
                previewing the ballot early.
              </p>
            </div>
          )}

          {teammates.length === 0 ? (
            <p className="text-center py-8 token-13 text-glass-muted">
              No eligible teammates found.
            </p>
          ) : (
            teammates.map((t) => {
              const isCurrentVote = myVote?.nominee_id === t.user_id;
              const badge = ROLE_BADGE[t.role] || ROLE_BADGE.closer;
              return (
                <button
                  key={t.user_id}
                  onClick={() => handleVote(t)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left ${
                    isCurrentVote
                      ? "bg-black/[0.04] border border-black/[0.08]"
                      : "hover:bg-black/[0.02] border border-transparent"
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: badge.avatarBg }}
                  >
                    <span className="token-14 font-medium" style={{ color: badge.text }}>
                      {t.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p
                      className={`token-14 truncate text-glass ${isCurrentVote ? "font-semibold" : ""}`}
                    >
                      {t.full_name}
                    </p>
                    <span
                      className="inline-block px-1.5 py-0.5 rounded token-9u"
                      style={{ backgroundColor: badge.bg, color: badge.text }}
                    >
                      {badge.label}
                    </span>
                  </div>

                  {isCurrentVote && (
                    <svg
                      className="w-5 h-5 text-glass flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default MvpVotingModal;
